import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { PointOfSale as CashierIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { formatDateTime } from '../utils/dateFormat';
import { CHECK_STATUSES, ALL_CHECK_STATUSES } from '../constants/appConstants';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/permissions';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import SelectField from '../components/inputs/SelectField';
import { formatCheckListAmount, getCheckStatusColor, getCheckStatusLabel } from '../utils/checkDisplay';
import { getClientDisplayName } from '../utils/orderItemDisplay';

const Cashiering = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isClient, isInternal, userRole, customPermissions } = useAuth();
  const canManageChecks = isInternal && hasPermission(userRole, 'manage_checks', customPermissions);
  const canRemoveCheckItem = isInternal && hasPermission(userRole, 'remove_check_item', customPermissions);
  const canViewChecks = canManageChecks || canRemoveCheckItem;

  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState([CHECK_STATUSES.OPEN, CHECK_STATUSES.PARTIALLY_PAID]);
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [clients, setClients] = useState([]);

  const fetchChecks = useCallback(async () => {
    if (isInternal && !canViewChecks) {
      setChecks([]);
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (statusFilter.length > 0) params.status = statusFilter.join(',');
      if (!isClient) {
        if (clientFilter) params.clientUser = clientFilter;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
      }
      const endpoint = isClient ? '/api/checks/my' : '/api/checks';
      const res = await axios.get(endpoint, { params });
      setChecks(res.data || []);
    } catch (error) {
      console.error('Failed to fetch checks:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, clientFilter, dateFrom, dateTo, isClient, isInternal, canViewChecks]);

  const fetchClients = useCallback(async () => {
    if (isClient) return;
    try {
      const res = await axios.get('/api/clients');
      setClients(res.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, [isClient]);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  useEffect(() => {
    if (!isClient) fetchClients();
  }, [fetchClients, isClient]);

  if (isInternal && !canViewChecks) {
    return (
      <Layout>
        <Container maxWidth="xl" disableGutters>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('users.noPermission')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  const openCheck = (checkId) => {
    const ids = checks.map((c) => c._id);
    navigate(`/cashiering/${checkId}`, { state: { ids, index: ids.indexOf(checkId) } });
  };

  return (
    <Layout>
      <Container maxWidth="xl" disableGutters>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <CashierIcon color="primary" fontSize="large" />
          <Typography variant="h4" fontWeight="bold">{isClient ? t('cashiering.myChecks') : t('cashiering.title')}</Typography>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <SelectField
              label={t('cashiering.checkStatus')}
              multiple
              value={statusFilter}
              onChange={(next) => setStatusFilter(next)}
              options={ALL_CHECK_STATUSES.map((s) => ({ value: s, label: getCheckStatusLabel(s, t) }))}
              emptyLabel={t('common.all')}
              sx={{ minWidth: isMobile ? '100%' : 220, flex: isMobile ? 1 : undefined }}
            />

            {!isClient && (
              <>
                <SelectField
                  label={t('cashiering.client')}
                  value={clientFilter}
                  onChange={(next) => setClientFilter(next)}
                  options={clients.map((c) => ({ value: c._id, label: getClientDisplayName(c) }))}
                  emptyLabel={t('cashiering.allClients')}
                  includeEmptyOption
                  sx={{ minWidth: isMobile ? '100%' : 200, flex: isMobile ? 1 : undefined }}
                />

                <TextField
                  size="small" type="date" label={t('common.from')} value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: isMobile ? 1 : undefined }}
                />
                <TextField
                  size="small" type="date" label={t('common.to')} value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: isMobile ? 1 : undefined }}
                />
              </>
            )}
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {checks.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>{t('cashiering.noChecksFound')}</Typography>
            ) : checks.map((check) => (
              <Paper
                key={check._id}
                variant="outlined"
                sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => openCheck(check._id)}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {check.name || t('cashiering.checkNumber', { id: check._id.slice(-6) })}
                    </Typography>
                    {!isClient && (
                      <Typography variant="body2" color="text.secondary">{getClientDisplayName(check.clientUser)}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">{check.createdAt ? formatDateTime(check.createdAt) : '—'}</Typography>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5} flexShrink={0}>
                    <Chip label={getCheckStatusLabel(check.status, t)} color={getCheckStatusColor(check.status)} size="small" />
                    <Typography variant="body2" fontWeight="bold" color={(check.amountDue || 0) > 0 ? 'error.main' : 'success.main'}>
                      {formatCheckListAmount(check.amountDue, check)}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    {t('cashiering.items')}: <strong>{check.itemCount || 0}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('cashiering.totalItems')}: <strong>{formatCheckListAmount(check.totalItems, check)}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('cashiering.payments')}: <strong>{formatCheckListAmount(check.totalDeposits, check)}</strong>
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('cashiering.name')}</TableCell>
                  {!isClient && <TableCell>{t('cashiering.client')}</TableCell>}
                  <TableCell>{t('cashiering.checkStatus')}</TableCell>
                  <TableCell>{t('cashiering.items')}</TableCell>
                  <TableCell>{t('cashiering.totalItems')}</TableCell>
                  <TableCell>{t('cashiering.payments')}</TableCell>
                  <TableCell>{t('cashiering.amountDue')}</TableCell>
                  <TableCell>{t('cashiering.created')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checks.map((check) => (
                  <TableRow
                    key={check._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openCheck(check._id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {check.name || t('cashiering.checkNumber', { id: check._id.slice(-6) })}
                      </Typography>
                    </TableCell>
                    {!isClient && <TableCell>{getClientDisplayName(check.clientUser)}</TableCell>}
                    <TableCell>
                      <Chip label={getCheckStatusLabel(check.status, t)} color={getCheckStatusColor(check.status)} size="small" />
                    </TableCell>
                    <TableCell>{check.itemCount || 0}</TableCell>
                    <TableCell>{formatCheckListAmount(check.totalItems, check)}</TableCell>
                    <TableCell>{formatCheckListAmount(check.totalDeposits, check)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color={(check.amountDue || 0) > 0 ? 'error.main' : 'success.main'}>
                        {formatCheckListAmount(check.amountDue, check)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{check.createdAt ? formatDateTime(check.createdAt) : '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {checks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isClient ? 7 : 8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('cashiering.noChecksFound')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Layout>
  );
};

export default Cashiering;
